import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  ServiceUser,
  ServiceUserCreate,
  ServiceUserUpdate,
} from "@/api/types";

export const SERVICE_USERS_KEY = ["service-users"] as const;

interface UseServiceUsersFilters {
  is_active?: boolean;
  q?: string;
}

export function useServiceUsers(filters: UseServiceUsersFilters = {}) {
  return useQuery({
    queryKey: [...SERVICE_USERS_KEY, filters],
    queryFn: () => {
      const params: Record<string, string | boolean> = {};
      if (filters.is_active !== undefined) params.is_active = filters.is_active;
      if (filters.q) params.q = filters.q;
      return api.GET<ServiceUser[]>("/service-users", { params });
    },
  });
}

export function useServiceUser(id: string) {
  return useQuery({
    queryKey: [...SERVICE_USERS_KEY, id],
    queryFn: () => api.GET<ServiceUser>(`/service-users/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateServiceUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ServiceUserCreate) =>
      api.POST<ServiceUser>("/service-users", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SERVICE_USERS_KEY });
    },
  });
}

export function useUpdateServiceUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ServiceUserUpdate) =>
      api.PATCH<ServiceUser>(`/service-users/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SERVICE_USERS_KEY });
      void qc.invalidateQueries({ queryKey: [...SERVICE_USERS_KEY, id] });
    },
  });
}
